import {
  ActionIcon,
  Box,
  Combobox,
  Group,
  Loader,
  ScrollArea,
  Text,
  TextInput,
  useCombobox,
} from "@mantine/core";
import { IconPhoto, IconSearch, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { API_ROUTE } from "../../const/apiRoute";
import { convertImageDomain } from "../../const/converhandle";
import { api } from "../../library/axios";
import { DragHandle, reorderList, SortableArea, useSortableItem } from "./HomeBlockShared";
import classes from "./homeBlock.module.css";

type ProductInfo = { code: string; name: string; image: string | null; price: number | null };

// Chuỗi lưu trong JSON: mã sản phẩm ngăn cách dấu phẩy, bỏ trống và trùng
export const parseCodes = (value: string) =>
  Array.from(new Set(value.split(",").map((code) => code.trim()).filter(Boolean)));

const joinCodes = (codes: string[]) => codes.join(",");

const toInfo = (item: any): ProductInfo | null => {
  const code = typeof item?.itemCode === "string" ? item.itemCode.trim() : "";
  if (!code) return null;
  return {
    code,
    name: String(item.itemName ?? code).trim(),
    image: typeof item.primaryImage === "string" && item.primaryImage ? convertImageDomain(item.primaryImage) : null,
    price: typeof item.unitSellingPrice === "number" ? item.unitSellingPrice : null,
  };
};

// Tìm theo tên hoặc mã ở danh sách sản phẩm. Gọi thẳng api để lỗi không bật thông báo mỗi lần gõ.
const searchProducts = async (keyword: string, take = 20): Promise<ProductInfo[]> => {
  const res = await api.get(
    `${API_ROUTE.GET_LIST_PRODUCT}?Skip=0&Take=${take}&KeySearch=${encodeURIComponent(keyword)}`
  );
  const list = res.data?.data;
  return Array.isArray(list) ? list.map(toInfo).filter((p): p is ProductInfo => !!p) : [];
};

// Thông tin sản phẩm đã biết theo mã, dùng chung cả màn để không tra lại
const known = new Map<string, ProductInfo>();
const lookups = new Map<string, Promise<void>>();

const lookupCode = (code: string) => {
  let request = lookups.get(code);
  if (!request) {
    request = searchProducts(code, 5)
      .then((found) => {
        const exact = found.find((p) => p.code.toLowerCase() === code.toLowerCase());
        if (exact) known.set(code, exact);
      })
      .catch(() => {
        lookups.delete(code);
      });
    lookups.set(code, request);
  }
  return request;
};

const formatPrice = (price: number | null) =>
  price === null ? "" : `${price.toLocaleString("vi-VN")} ₫`;

const ProductThumb = ({ src }: { src: string | null }) => (
  <span className={classes.productThumb} aria-hidden>
    {src ? <img src={src} alt="" /> : <IconPhoto size={16} />}
  </span>
);

const PickedRow = ({
  code,
  info,
  fallbackName,
  onRemove,
}: {
  code: string;
  info?: ProductInfo;
  fallbackName?: string;
  onRemove: () => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(code);
  const name = info?.name ?? fallbackName;
  return (
    <div ref={setNodeRef} className={classes.pickedRow} style={style}>
      <DragHandle label={`Kéo để đổi thứ tự ${code}`} handleProps={handleProps} size={14} />
      <ProductThumb src={info?.image ?? null} />
      <Box miw={0} style={{ flex: "1 1 auto" }}>
        <Text fz={13} truncate c={name ? undefined : "dimmed"}>
          {name ?? "Chưa lấy được tên sản phẩm"}
        </Text>
        <Text fz={12} className={`${classes.mono} ${classes.muted}`}>
          {code}
          {info?.price !== null && info?.price !== undefined && ` · ${formatPrice(info.price)}`}
        </Text>
      </Box>
      <ActionIcon variant="subtle" color="gray" aria-label={`Bỏ ${code}`} onClick={onRemove}>
        <IconX size={14} />
      </ActionIcon>
    </div>
  );
};

type ProductCodePickerProps = {
  // Mã sản phẩm ngăn cách dấu phẩy
  value: string;
  onChange: (value: string) => void;
  // Tên dùng tạm cho dữ liệu mẫu khi chưa tra được từ API
  fallbackNames?: Record<string, string>;
};

export const ProductCodePicker = ({ value, onChange, fallbackNames }: ProductCodePickerProps) => {
  const codes = parseCodes(value);
  const [, setVersion] = useState(0);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ProductInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const requestRef = useRef(0);
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });

  // Tra tên, ảnh, giá cho các mã chưa biết (mã đã lưu từ trước hoặc dán tay)
  const codesKey = codes.join(",");
  useEffect(() => {
    const missing = codesKey.split(",").filter((code) => code && !known.has(code));
    if (missing.length === 0) return;
    let alive = true;
    Promise.all(missing.map(lookupCode)).then(() => alive && setVersion((v) => v + 1));
    return () => {
      alive = false;
    };
  }, [codesKey]);

  useEffect(() => {
    const keyword = search.trim();
    if (keyword.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    const requestId = ++requestRef.current;
    setSearching(true);
    const timer = window.setTimeout(() => {
      searchProducts(keyword)
        .then((found) => {
          if (requestId !== requestRef.current) return;
          found.forEach((p) => known.set(p.code, p));
          setResults(found);
          setSearchFailed(false);
        })
        .catch(() => {
          if (requestId !== requestRef.current) return;
          setResults([]);
          setSearchFailed(true);
        })
        .finally(() => requestId === requestRef.current && setSearching(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const chosen = new Set(codes);

  return (
    <Box>
      <Combobox
        store={combobox}
        withinPortal
        onOptionSubmit={(code) => {
          if (!chosen.has(code)) onChange(joinCodes([...codes, code]));
          combobox.closeDropdown();
          setSearch("");
        }}
      >
        <Combobox.Target>
          <TextInput
            label="Thêm sản phẩm"
            placeholder="Gõ ít nhất 2 ký tự: tên hoặc mã sản phẩm"
            leftSection={<IconSearch size={16} />}
            rightSection={searching ? <Loader size={14} /> : undefined}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            onFocus={() => search.trim().length >= 2 && combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
          />
        </Combobox.Target>
        <Combobox.Dropdown hidden={search.trim().length < 2}>
          <Combobox.Options>
            <ScrollArea.Autosize mah={320} type="auto">
              {results.length > 0 ? (
                results.map((p) => (
                  <Combobox.Option value={p.code} key={p.code} disabled={chosen.has(p.code)}>
                    <Group gap={10} wrap="nowrap">
                      <ProductThumb src={p.image} />
                      <Box miw={0} style={{ flex: "1 1 auto" }}>
                        <Text fz={13} truncate>
                          {p.name}
                        </Text>
                        <Text fz={12} className={`${classes.mono} ${classes.muted}`}>
                          {p.code}
                          {p.price !== null && ` · ${formatPrice(p.price)}`}
                          {chosen.has(p.code) && " · Đã chọn"}
                        </Text>
                      </Box>
                    </Group>
                  </Combobox.Option>
                ))
              ) : (
                <Combobox.Empty>
                  {searching
                    ? "Đang tìm..."
                    : searchFailed
                      ? "Không gọi được API sản phẩm"
                      : "Không có sản phẩm khớp"}
                </Combobox.Empty>
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {codes.length > 0 ? (
        <Box mt={10} className={classes.pickedList}>
          <SortableArea
            ids={codes}
            onReorder={(from, to) => onChange(joinCodes(reorderList(codes, from, to)))}
          >
            {codes.map((code) => (
              <PickedRow
                key={code}
                code={code}
                info={known.get(code)}
                fallbackName={fallbackNames?.[code]}
                onRemove={() => onChange(joinCodes(codes.filter((c) => c !== code)))}
              />
            ))}
          </SortableArea>
        </Box>
      ) : (
        <Text fz={13} mt={10} className={classes.muted}>
          Chưa chọn sản phẩm nào. Tab trống sẽ ẩn trên web.
        </Text>
      )}

      <TextInput
        mt={12}
        label="Mã sản phẩm sẽ lưu"
        description="Ngăn cách bằng dấu phẩy, theo đúng thứ tự hiện trên web. Có thể dán danh sách mã vào đây."
        inputWrapperOrder={["label", "input", "description"]}
        classNames={{ input: classes.mono }}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={() => onChange(joinCodes(parseCodes(value)))}
      />
    </Box>
  );
};
